import * as React from 'react'

import { useMutation } from '@apollo/react-hooks'
import { ApolloError } from 'apollo-client'
import gql from 'graphql-tag'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { LOCIZE_NAMESPACE } from 'main/common'
import Form, { FormGroup } from 'main/components/common/Form'
import { TextField } from 'main/ui'
import { UserSignUpSource } from 'main/util/user'

import { i18nKeyPrefix as PRE } from '../../.'
import { ErrorMessage, GroupWrapper, SubmitButton } from './SignupForm.styles'

interface IProps extends RouteComponentProps {
  email: string | null
}

export const CREATE_STAFF_USER_MUTATION = gql`
  mutation CreateStaffUser($user: CreateParentUserParams!) {
    createParentUser(user: $user) {
      user {
        id
      }
    }
  }
`

function SignupForm({ email, history }: IProps) {
  const [firstName, setFirstName] = React.useState<string>('')
  const [lastName, setLastName] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [signUpError, setSignUpError] = React.useState<string>('')
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const [signUp, { loading }] = useMutation(CREATE_STAFF_USER_MUTATION, {
    onCompleted: () => history.push('/staff-accept/download-app'),
    onError: error => handleError(error),
  })

  React.useEffect(() => {
    if (!email) {
      setSignUpError(t(`${PRE}-FORM-INVALID-LINK-ERROR`))
    }
  }, [])

  return (
    <Form onSubmit={onSubmit}>
      <GroupWrapper>
        <FormGroup>
          <TextField
            value={firstName}
            label={t(`${PRE}-FORM-FIRST-NAME-LBL`)}
            placeholder={t(`${PRE}-FORM-FIRST-NAME-PLACEHOLDER`)}
            required
            onChange={name => setFirstName(name)}
          />

          <TextField
            value={lastName}
            label={t(`${PRE}-FORM-LAST-NAME-LBL`)}
            placeholder={t(`${PRE}-FORM-LAST-NAME-PLACEHOLDER`)}
            required
            onChange={newLastName => setLastName(newLastName)}
          />
        </FormGroup>
      </GroupWrapper>
      <GroupWrapper>
        <FormGroup>
          <TextField
            disabled
            value={email || ''}
            label={t(`${PRE}-FORM-EMAIL-LBL`)}
            placeholder={t(`${PRE}-FORM-EMAIL-PLACEHOLDER`)}
            onChange={() => null}
          />
          <TextField
            value={password}
            type="password"
            label={t(`${PRE}-FORM-PASSWORD-LBL`)}
            placeholder={t(`${PRE}-FORM-PASSWORD-PLACEHOLDER`)}
            required
            onChange={pwd => setPassword(pwd)}
          />
        </FormGroup>
      </GroupWrapper>
      <SubmitButton
        disabled={password === '' || firstName === '' || lastName === ''}
        full
        kind="regular"
        pending={loading}
      >
        {t(`${PRE}-FORM-BTN`)}
      </SubmitButton>
      {signUpError && <ErrorMessage>{signUpError}</ErrorMessage>}
    </Form>
  )

  function onSubmit(): void {
    signUp({
      variables: {
        user: {
          email,
          firstName,
          lastName,
          password,
          signUpSource: UserSignUpSource.StaffInvitationPage,
          parentTosAgreed: true,
          family: {},
        },
      },
    })
  }

  function handleError(error: ApolloError): void {
    const graphQLError = error.graphQLErrors[0]
    const errorToLocizeKeys = new Map([
      ['email has already been taken', `${PRE}-FORM-ALREADY-TAKEN-ERROR`],
    ])

    if (!errorToLocizeKeys.has(graphQLError.message)) {
      setSignUpError(t(`${PRE}-FORM-GENERIC-ERROR`))
    } else {
      setSignUpError(t(errorToLocizeKeys.get(graphQLError.message) || ''))
    }
  }
}

export default withRouter(SignupForm)
